/*
  <media-gallery data-lightbox="lightbox-id">
  - plays muted preview videos only while they are on screen
  - opens a lightbox (shared <drawer-panel>) with the full video from a <template>
  - scroll-linked motion: gentle parallax drift (staggered) or straightening tilt (scroll_tilt)
*/
(() => {
  if (customElements.get('media-gallery')) return;

  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.lightbox = document.getElementById(this.dataset.lightbox);
      this.body = this.lightbox && this.lightbox.querySelector('[data-mg-lightbox-body]');
      this.items = Array.from(this.querySelectorAll('.mg__item'));

      this.onClick = (e) => {
        const btn = e.target.closest('[data-mg-open]');
        if (!btn || !this.lightbox || !this.body) return;
        const tpl = document.getElementById(btn.dataset.mgOpen);
        if (!tpl) return;
        this.body.replaceChildren(tpl.content.cloneNode(true));
        Theme.openDrawer(this.lightbox.id, btn);
        const video = this.body.querySelector('video');
        if (video) video.play().catch(() => {});
      };
      this.onClose = () => { if (this.body) this.body.replaceChildren(); };
      this.addEventListener('click', this.onClick);
      if (this.lightbox) this.lightbox.addEventListener('drawer:close', this.onClose);

      this.setupPreviews();
      this.setupMotion();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      if (this.lightbox) this.lightbox.removeEventListener('drawer:close', this.onClose);
      if (this.previewObserver) this.previewObserver.disconnect();
      if (this.motionObserver) this.motionObserver.disconnect();
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    }

    setupPreviews() {
      const videos = Array.from(this.querySelectorAll('video.mg__video'));
      if (!videos.length) return;
      this.previewObserver = new IntersectionObserver((entries) => {
        entries.forEach(({ target, isIntersecting }) => {
          if (isIntersecting && !reduced()) {
            target.muted = true;
            target.play().catch(() => {});
          } else {
            target.pause();
          }
        });
      }, { threshold: 0.25 });
      videos.forEach((v) => this.previewObserver.observe(v));
    }

    setupMotion() {
      const staggered = this.classList.contains('mg--staggered');
      const scrollTilt = this.classList.contains('mg--scroll_tilt');
      if ((!staggered && !scrollTilt) || reduced()) return;

      this.queued = false;
      this.onScroll = () => {
        if (this.queued) return;
        this.queued = true;
        requestAnimationFrame(() => {
          this.queued = false;
          const rect = this.getBoundingClientRect();
          const vh = window.innerHeight;
          // -1 as the gallery enters from below, 0 at viewport center, 1 as it leaves at the top
          const centered = clamp(((vh / 2) - (rect.top + rect.height / 2)) / ((vh + rect.height) / 2), -1, 1);
          if (scrollTilt) {
            // Fully tilted below the fold, straight once the gallery reaches the middle of the screen.
            this.style.setProperty('--mg-tilt', clamp(-centered * 1.6, 0, 1).toFixed(3));
          }
          if (staggered && window.innerWidth >= 750) {
            this.items.forEach((item, i) => {
              const dir = i % 2 === 0 ? 1 : -1;
              item.style.setProperty('--mg-shift', `${(centered * 26 * dir).toFixed(1)}px`);
            });
          }
        });
      };
      this.motionObserver = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          window.addEventListener('scroll', this.onScroll, { passive: true });
          this.onScroll();
        } else {
          window.removeEventListener('scroll', this.onScroll);
        }
      });
      this.motionObserver.observe(this);
      this.onScroll();
    }
  }

  customElements.define('media-gallery', MediaGallery);
})();
